import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { CareConciergeFormData, UrgencyLevel } from './types';
import { formatUSPhone } from '@/shared/utils/formatters';
import ClientContactFields from './ClientContactFields';

type FinancialAdvisorWorkflowProps = {
  register: UseFormRegister<CareConciergeFormData>;
  errors: FieldErrors<CareConciergeFormData>;
  urgencyLevel?: UrgencyLevel;
  /** Feature 104: raw watched value — radios submit 'true'/'false' strings. */
  advisorStayLooped?: boolean | string;
};

const FinancialAdvisorWorkflow: React.FC<FinancialAdvisorWorkflowProps> = ({
  register,
  errors,
  urgencyLevel,
  advisorStayLooped,
}) => {
  const showDatePicker = urgencyLevel === 'IMMEDIATE_CRISIS' || urgencyLevel === 'CRISIS';

  return (
    <div className="space-y-8">
      {/* Advisor Information */}
      <div>
        <h4 className="text-xl font-recoleta font-semibold text-[#111827] mb-4">
          Your Information
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Your Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('requestorName', { required: 'Name is required' })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            />
            {errors.requestorName && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.requestorName.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Firm Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('advisorFirmName', { required: 'Firm name is required' })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            />
            {errors.advisorFirmName && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.advisorFirmName.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              {...register('requestorEmail', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            />
            {errors.requestorEmail && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.requestorEmail.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              placeholder="(555) 123-4567"
              {...register('requestorPhone', {
                required: 'Phone number is required',
                pattern: {
                  value: /^\(\d{3}\) \d{3}-\d{4}$/,
                  message: 'Enter a valid US phone number',
                },
                onChange: (e) => {
                  e.target.value = formatUSPhone(e.target.value);
                },
              })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            />
            {errors.requestorPhone && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.requestorPhone.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Client Information */}
      <div>
        <h4 className="text-xl font-recoleta font-semibold text-[#111827] mb-4">
          Client Information
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Client First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('clientFirstName', { required: 'Client first name is required' })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            />
            {errors.clientFirstName && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.clientFirstName.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Client Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('clientLastName', { required: 'Client last name is required' })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            />
            {errors.clientLastName && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.clientLastName.message}</p>
            )}
          </div>

          <ClientContactFields
            register={register}
            errors={errors}
            advisorStayLooped={advisorStayLooped}
          />
        </div>
      </div>

      {/* Care Recipient Details */}
      <div>
        <h4 className="text-xl font-recoleta font-semibold text-[#111827] mb-4">
          About the Person Needing Support
        </h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Who needs support? <span className="text-red-500">*</span>
            </label>
            <select
              {...register('careRecipientRelation', { required: 'Please select who needs support' })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            >
              <option value="">Select...</option>
              <option value="CLIENT">My client</option>
              <option value="CLIENT_PARENT">My client's parent</option>
              <option value="CLIENT_SPOUSE">My client's spouse/partner</option>
              <option value="OTHER">Other</option>
            </select>
            {errors.careRecipientRelation && (
              <p className="mt-1 text-sm text-red-600 font-termina">
                {errors.careRecipientRelation.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Age and general status <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('careRecipientAgeStatus', {
                required: 'Please provide age and general status',
                maxLength: {
                  value: 200,
                  message: 'Description must be 200 characters or less',
                },
              })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
              maxLength={200}
              placeholder="E.g., 78 years old, recovering from stroke"
            />
            {errors.careRecipientAgeStatus && (
              <p className="mt-1 text-sm text-red-600 font-termina">
                {errors.careRecipientAgeStatus.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Brief description of the situation <span className="text-red-500">*</span>
            </label>
            <textarea
              {...register('situationDescription', {
                required: 'Please describe the situation',
                maxLength: {
                  value: 500,
                  message: 'Description must be 500 characters or less',
                },
              })}
              rows={4}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm resize-none"
              maxLength={500}
              placeholder="Tell us briefly about the type of help needed..."
            />
            {errors.situationDescription && (
              <p className="mt-1 text-sm text-red-600 font-termina">
                {errors.situationDescription.message}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Urgency Assessment */}
      <div>
        <h4 className="text-xl font-recoleta font-semibold text-[#111827] mb-4">Urgency</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              How urgent is this need? <span className="text-red-500">*</span>
            </label>
            <select
              {...register('urgencyLevel', { required: 'Please select urgency level' })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            >
              <option value="">Select urgency...</option>
              <option value="CRISIS">Crisis - support needed immediately</option>
              <option value="WITHIN_WEEK">Within the next week</option>
              <option value="WITHIN_MONTH">Within the next month</option>
              <option value="PLANNING_AHEAD">Planning ahead</option>
            </select>
            {errors.urgencyLevel && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.urgencyLevel.message}</p>
            )}
          </div>

          {showDatePicker && (
            <div>
              <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
                Service needed by date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                {...register('serviceNeededByDate', {
                  required: showDatePicker ? 'Please provide a date for crisis urgency' : false,
                })}
                className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
                min={new Date().toISOString().split('T')[0]}
              />
              {errors.serviceNeededByDate && (
                <p className="mt-1 text-sm text-red-600 font-termina">
                  {errors.serviceNeededByDate.message}
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Is the person currently safe? <span className="text-red-500">*</span>
            </label>
            <select
              {...register('safetyStatus', { required: 'Please indicate safety status' })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            >
              <option value="">Select...</option>
              <option value="YES">Yes</option>
              <option value="NO">No</option>
              <option value="UNSURE">Unsure</option>
            </select>
            {errors.safetyStatus && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.safetyStatus.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Location */}
      <div>
        <h4 className="text-xl font-recoleta font-semibold text-[#111827] mb-4">Location</h4>
        <div>
          <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
            Where does the person needing support currently live? <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('currentLocation', { required: 'Current location is required' })}
            className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            placeholder="City, State and ZIP"
          />
          {errors.currentLocation && (
            <p className="mt-1 text-sm text-red-600 font-termina">{errors.currentLocation.message}</p>
          )}
        </div>
      </div>

      {/* Advisor Involvement */}
      <div>
        <h4 className="text-xl font-recoleta font-semibold text-[#111827] mb-4">
          Your Involvement
        </h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Would you like to stay looped in on recommendations? <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  value="true"
                  {...register('advisorStayLooped', { required: 'Please indicate your preference' })}
                  className="w-4 h-4 text-[#026857] border-[#E5E7EB] focus:ring-[#026857]"
                />
                <span className="font-termina text-sm text-[#111827]">Yes</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  value="false"
                  {...register('advisorStayLooped', { required: 'Please indicate your preference' })}
                  className="w-4 h-4 text-[#026857] border-[#E5E7EB] focus:ring-[#026857]"
                />
                <span className="font-termina text-sm text-[#111827]">No</span>
              </label>
            </div>
            {errors.advisorStayLooped && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.advisorStayLooped.message}</p>
            )}
          </div>

          <div className="bg-[#F3F4F6] p-4 rounded-lg border border-[#E5E7EB]">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                {...register('clientPermissionConfirmed', {
                  required: 'You must confirm client permission to submit this request',
                })}
                className="mt-1 w-5 h-5 text-[#026857] border-[#E5E7EB] rounded focus:ring-[#026857]"
              />
              <div className="flex-1">
                <span className="block font-termina font-medium text-[#111827]">
                  I confirm that my client has given permission to submit this request on their behalf{' '}
                  <span className="text-red-500">*</span>
                </span>
                <span className="block text-sm text-[#6B7280] font-termina mt-1">
                  This confirmation is required before submitting
                </span>
              </div>
            </label>
            {errors.clientPermissionConfirmed && (
              <p className="mt-2 text-sm text-red-600 font-termina">
                {errors.clientPermissionConfirmed.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Additional notes (Optional)
            </label>
            <textarea
              {...register('additionalNotes', {
                maxLength: {
                  value: 1000,
                  message: 'Notes must be 1000 characters or less',
                },
              })}
              rows={4}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm resize-none"
              maxLength={1000}
              placeholder="Any additional context or notes you'd like to share..."
            />
            {errors.additionalNotes && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.additionalNotes.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Additional Details */}
      <div>
        <h4 className="text-xl font-recoleta font-semibold text-[#111827] mb-4">
          Additional Details
        </h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Categories of interest
            </label>
            <input
              type="text"
              {...register('categories')}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
              placeholder="e.g. Home Care, Memory Care (comma-separated)"
            />
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Special needs or requirements
            </label>
            <input
              type="text"
              {...register('specialNeeds', {
                maxLength: { value: 500, message: 'Special needs must be 500 characters or less' },
              })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
              maxLength={500}
              placeholder="e.g. Wheelchair accessibility, dietary restrictions"
            />
            {errors.specialNeeds && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.specialNeeds.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              When is care needed?
            </label>
            <input
              type="date"
              {...register('careStartDate')}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialAdvisorWorkflow;
